(() => {
  const MIN_ZOOM = 1;
  const MAX_ZOOM = 5;
  const DEFAULT_ZOOM = 1.5;

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  const preventDefault = (e) => {
    if (e && typeof e.cancelable === 'boolean' && e.cancelable) e.preventDefault();
  };

  const isMobileLike = () => window.matchMedia?.('(max-width: 767px)').matches;

  const touchToPoint = (touch) => (touch ? { x: touch.clientX, y: touch.clientY } : null);

  const getDistance = (p1, p2) => {
    const dx = p1.x - p2.x;
    const dy = p1.y - p2.y;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getMidpoint = (p1, p2) => ({ x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 });

  class DragZoomWrapper extends HTMLElement {
    #controller = new AbortController();

    #scale = DEFAULT_ZOOM;
    #initialDistance = 0;
    #startScale = DEFAULT_ZOOM;

    #translate = { x: 0, y: 0 };
    #startPosition = { x: 0, y: 0 };
    #startTranslate = { x: 0, y: 0 };

    #isDragging = false;
    #initialized = false;
    #animationFrame = null;
    #lastObservedSize = { width: 0, height: 0 };

    get #media () {
      return this.querySelector('img, video');
    }

    #handleResize = ([entry]) => {
      const width = entry?.contentRect?.width || 0;
      const height = entry?.contentRect?.height || 0;

      if (!width || !height) return;
      if (width === this.#lastObservedSize.width && height === this.#lastObservedSize.height) return;

      this.#lastObservedSize = { width, height };

      if (!this.#initialized && isMobileLike()) this.#initEventListeners();
      if (this.#initialized) this.#requestUpdateTransform();
    };

    #resizeObserver = window.ResizeObserver ? new ResizeObserver(this.#handleResize) : null;

    connectedCallback () {
      if (!this.#media) return;

      if (!this.#controller || this.#controller.signal.aborted) {
        this.#controller = new AbortController();
        this.#initialized = false;
      }

      this.#resizeObserver?.observe?.(this);

      if (isMobileLike()) {
        this.#initEventListeners();
        this.#updateTransform();
      } else {
        this.#scale = 1;
        this.#updateTransform();
      }
    }

    disconnectedCallback () {
      try { this.#controller.abort(); } catch {}
      this.#cancelAnimationFrame();
      this.#resizeObserver?.disconnect?.();
      this.#initialized = false;
    }

    destroy () {
      try { this.#controller.abort(); } catch {}
      this.#cancelAnimationFrame();
      this.#resizeObserver?.disconnect?.();
      this.#initialized = false;
    }

    #initEventListeners () {
      if (this.#initialized) return;

      this.#initialized = true;

      const { signal } = this.#controller;
      const options = { passive: false, signal };
      this.addEventListener('click', (e) => e.stopPropagation(), { signal });
      this.addEventListener('touchstart', this.#handleTouchStart, options);
      this.addEventListener('touchmove', this.#handleTouchMove, options);
      this.addEventListener('touchend', this.#handleTouchEnd, options);

      const media = this.#media;

      if (media?.tagName === 'IMG') {
        media.addEventListener('load', () => this.#requestUpdateTransform(), { signal });
      } else if (media?.tagName === 'VIDEO') {
        media.addEventListener('loadedmetadata', () => this.#requestUpdateTransform(), { signal });
        media.addEventListener('loadeddata', () => this.#requestUpdateTransform(), { signal });
      }
    }

    #handleTouchStart = (event) => {
      preventDefault(event);

      if (event.touches.length === 2) {
        const [p1, p2] = Array.from(event.touches).map(touchToPoint);
        if (!p1 || !p2) return;
        this.#startZoomGesture(p1, p2);
        return;
      }

      if (event.touches.length === 1) {
        const p = touchToPoint(event.touches[0]);
        if (!p) return;
        this.#startDragGesture(p);
      }
    };

    #startZoomGesture (p1, p2) {
      this.#initialDistance = getDistance(p1, p2);
      this.#startScale = this.#scale;
      this.#isDragging = false;
    }

    #startDragGesture (p) {
      this.#startPosition = { x: p.x, y: p.y };
      this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
      this.#isDragging = true;
    }

    #handleTouchMove = (event) => {
      preventDefault(event);

      const t = event.touches?.length || 0;
      const isZooming = t === 2;
      const isDragging = t === 1 && this.#isDragging;

      if (isZooming) this.#processZoomGesture(event);
      else if (isDragging) this.#processDragGesture(event);
    };

    #processZoomGesture (event) {
      const [p1, p2] = Array.from(event.touches).map(touchToPoint);
      if (!p1 || !p2) return;

      const currentMid = getMidpoint(p1, p2);
      const currentDist = getDistance(p1, p2);

      const oldScale = this.#scale;
      const newScale = (currentDist / this.#initialDistance) * this.#startScale;

      this.#scale = clamp(newScale, MIN_ZOOM, MAX_ZOOM);

      const containerCenter = { x: this.clientWidth / 2, y: this.clientHeight / 2 };
      const distFromCenter = {
        x: currentMid.x - containerCenter.x,
        y: currentMid.y - containerCenter.y,
      };

      const scaleDelta = this.#scale / oldScale - 1;

      this.#translate.x -= (distFromCenter.x * scaleDelta) / this.#scale;
      this.#translate.y -= (distFromCenter.y * scaleDelta) / this.#scale;

      this.#requestUpdateTransform();
      this.#isDragging = false;
    }

    #processDragGesture (event) {
      const p = touchToPoint(event.touches[0]);
      if (!p) return;

      this.#translate = {
        x: this.#startTranslate.x + (p.x - this.#startPosition.x) / this.#scale,
        y: this.#startTranslate.y + (p.y - this.#startPosition.y) / this.#scale,
      };

      this.#requestUpdateTransform();
    }

    #handleTouchEnd = (event) => {
      if (event.touches.length === 0) {
        this.#isDragging = false;
        this.#requestUpdateTransform();
      }
    };

    #getMinZoom () {
      const cw = this.clientWidth;
      const ch = this.clientHeight;
      const dims = this.#getMediaDimensions();
      if (!dims || !cw || !ch) return null;
      const { width: iw, height: ih } = dims;
      return Math.max(cw / iw, ch / ih) + 0.001;
    }

    #getMediaDimensions () {
      const el = this.#media;
      if (!el) return null;

      const cw = this.clientWidth;
      const ch = this.clientHeight;
      if (!cw || !ch) return null;

      let naturalWidth = 0;
      let naturalHeight = 0;

      if (el.tagName === 'IMG') {
        naturalWidth = el.naturalWidth || 0;
        naturalHeight = el.naturalHeight || 0;
      } else if (el.tagName === 'VIDEO') {
        naturalWidth = el.videoWidth || 0;
        naturalHeight = el.videoHeight || 0;
      }

      if (!naturalWidth || !naturalHeight) return null;

      const rect = this.getBoundingClientRect();
      const containerAspect = rect.width / rect.height;
      const naturalAspect = naturalWidth / naturalHeight;

      let w, h;
      if (naturalAspect > containerAspect) {
        w = rect.width;
        h = w / naturalAspect;
      } else {
        h = rect.height;
        w = h * naturalAspect;
      }

      if (!w || !h) return null;
      return { width: w, height: h };
    }

    #constrainTranslation () {
      const cw = this.clientWidth;
      const ch = this.clientHeight;
      if (!cw || !ch) return;

      const minZoom = this.#getMinZoom();
      if (!minZoom) return;

      this.#scale = clamp(this.#scale, minZoom, MAX_ZOOM);

      const dims = this.#getMediaDimensions();
      if (!dims) return;

      const { width: iw, height: ih } = dims;
      const scaledW = iw * this.#scale;
      const scaledH = ih * this.#scale;

      const excessW = Math.max(0, scaledW - cw);
      const excessH = Math.max(0, scaledH - ch);

      const maxTx = excessW / (2 * this.#scale);
      const maxTy = excessH / (2 * this.#scale);

      this.#translate.x = clamp(this.#translate.x, -maxTx, maxTx);
      this.#translate.y = clamp(this.#translate.y, -maxTy, maxTy);
    }

    #requestUpdateTransform = () => {
      if (this.#animationFrame) return;
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    };

    #cancelAnimationFrame () {
      if (!this.#animationFrame) return;
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }

    #updateTransform = () => {
      this.#animationFrame = null;
      if (!this.clientWidth || !this.clientHeight) return;

      this.#constrainTranslation();

      this.style.setProperty('--drag-zoom-scale', String(this.#scale));
      this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
      this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
    };
  }

  if (!customElements.get('drag-zoom-wrapper')) {
    customElements.define('drag-zoom-wrapper', DragZoomWrapper);
  }
})();
